import { Auth0Provider, useAuth0, User as Auth0User } from '@auth0/auth0-react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { env } from '@/utils/env';

type UserProfile = Auth0User | { name: string; email?: string } | null;

type AuthContextValue = {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: UserProfile;
  getAccessToken: () => Promise<string | null>;
  login: () => Promise<void>;
  logout: () => void;
};

type AuthConfig = {
  domain: string;
  clientId: string;
  audience?: string;
  scope?: string;
};

const fallbackAuth: AuthContextValue = {
  isLoading: false,
  isAuthenticated: false,
  user: null,
  getAccessToken: async () => null,
  login: async () => undefined,
  logout: () => undefined,
};

const AuthContext = createContext<AuthContextValue>(fallbackAuth);

const defaultScope = 'openid profile email';

const normalizeAuthConfig = (partial?: Partial<AuthConfig> | null): AuthConfig | null => {
  if (!partial?.domain || !partial?.clientId) {
    return null;
  }
  return {
    domain: partial.domain,
    clientId: partial.clientId,
    audience: partial.audience ?? undefined,
    scope: partial.scope ?? defaultScope,
  };
};

const AuthBridge = ({
  children,
  config,
  configLoading,
  queuedLogin,
  onLoginConsumed,
}: {
  children: ReactNode;
  config: AuthConfig;
  configLoading: boolean;
  queuedLogin: boolean;
  onLoginConsumed: () => void;
}) => {
  const {
    isLoading,
    isAuthenticated,
    user,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();

  const combinedLoading = isLoading || configLoading;

  const value = useMemo<AuthContextValue>(
    () => ({
      isLoading: combinedLoading,
      isAuthenticated,
      user: user ?? null,
      getAccessToken: async () => {
        try {
          const token = await getAccessTokenSilently();
          return token;
        } catch (error) {
          console.warn('Failed to fetch Auth0 token', error);
          return null;
        }
      },
      login: async () => {
        await loginWithRedirect({
          authorizationParams: {
            audience: config.audience,
            scope: config.scope ?? defaultScope,
            redirect_uri: window.location.origin,
          },
        });
      },
      logout: () => {
        logout({ logoutParams: { returnTo: window.location.origin } });
      },
    }),
    [
      combinedLoading,
      config.audience,
      config.scope,
      getAccessTokenSilently,
      isAuthenticated,
      loginWithRedirect,
      logout,
      user,
    ],
  );

  useEffect(() => {
    if (!queuedLogin || combinedLoading) {
      return;
    }

    if (isAuthenticated) {
      onLoginConsumed();
      return;
    }

    loginWithRedirect({
      authorizationParams: {
        audience: config.audience,
        scope: config.scope ?? defaultScope,
        redirect_uri: window.location.origin,
      },
    })
      .catch((error) => {
        console.warn('Auth login redirect failed', error);
      })
      .finally(() => {
        onLoginConsumed();
      });
  }, [
    combinedLoading,
    config.audience,
    config.scope,
    isAuthenticated,
    loginWithRedirect,
    onLoginConsumed,
    queuedLogin,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const envConfig = useMemo(
    () =>
      normalizeAuthConfig({
        domain: env.auth0Domain,
        clientId: env.auth0ClientId,
        audience: env.auth0Audience,
        scope: env.auth0Scope,
      }),
    [],
  );

  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(envConfig);
  const [configLoading, setConfigLoading] = useState<boolean>(!envConfig);
  const [queuedLogin, setQueuedLogin] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      setConfigLoading(true);
      const response = await fetch('/api/config');
      if (!response.ok) {
        throw new Error(`Config request failed: ${response.status}`);
      }
      const json = await response.json();
      const config = normalizeAuthConfig({
        domain: json?.auth0?.domain,
        clientId: json?.auth0?.clientId,
        audience: json?.auth0?.audience,
        scope: json?.auth0?.scope,
      });
      if (config) {
        setAuthConfig(config);
      } else {
        console.warn('Auth0 configuration missing required fields.');
      }
    } catch (error) {
      console.warn('Unable to load Auth0 config from /api/config', error);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const handleLoginConsumed = useCallback(() => {
    setQueuedLogin(false);
  }, []);

  useEffect(() => {
    if (!authConfig) {
      loadConfig().catch((error) => {
        console.warn('Auth config fetch failed', error);
      });
    }
  }, [authConfig, loadConfig]);

  const pendingValue = useMemo<AuthContextValue>(
    () => ({
      isLoading: configLoading,
      isAuthenticated: false,
      user: null,
      getAccessToken: async () => null,
      login: async () => {
        setQueuedLogin(true);
        await loadConfig();
      },
      logout: () => undefined,
    }),
    [configLoading, loadConfig, setQueuedLogin],
  );

  if (!authConfig) {
    return <AuthContext.Provider value={pendingValue}>{children}</AuthContext.Provider>;
  }

  return (
    <Auth0Provider
      domain={authConfig.domain}
      clientId={authConfig.clientId}
      authorizationParams={{
        audience: authConfig.audience,
        scope: authConfig.scope ?? defaultScope,
        redirect_uri: window.location.origin,
      }}
    >
      <AuthBridge
        config={authConfig}
        configLoading={configLoading}
        queuedLogin={queuedLogin}
        onLoginConsumed={handleLoginConsumed}
      >
        {children}
      </AuthBridge>
    </Auth0Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
